import { useEffect, useState } from "react";
import {
  analyticsConfigured,
  COOKIE_SETTINGS_EVENT,
  getAnalyticsConsent,
  setAnalyticsConsent,
  track
} from "../lib/analytics.js";
import { useT } from "../lib/i18n/store.js";
import { buildPath } from "../lib/route.js";
import type { Page } from "../types.js";

/**
 * Analytics-cookie consent. Shown until the visitor chooses (and again from the
 * footer's "Cookie settings"). Until they accept, analytics run cookieless and
 * personless (lib/analytics.ts); only strictly necessary storage (sign-in,
 * settings, the choice itself) is used. Not rendered at all when no analytics
 * project is configured.
 *
 * `compact` (the player): a one-line question pinned to the TOP of the screen,
 * so it never covers the choices at the bottom while the visitor reads. Same
 * two equal buttons and the same consent; the policy opens in a new tab so the
 * story stays where it was.
 */
export function CookieBanner({
  onNavigate,
  configured = analyticsConfigured,
  compact = false
}: {
  onNavigate: (page: Page) => void;
  /** Injectable for tests; defaults to whether a PostHog key is configured. */
  configured?: boolean;
  compact?: boolean;
}) {
  const t = useT();
  const [open, setOpen] = useState(() => configured && getAnalyticsConsent() === null);

  useEffect(() => {
    if (!configured) return;
    const reopen = () => setOpen(true);
    window.addEventListener(COOKIE_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, reopen);
  }, [configured]);

  if (!open) return null;

  function choose(consent: "granted" | "denied"): void {
    const previous = getAnalyticsConsent() ?? "none";
    setAnalyticsConsent(consent);
    // After the switch, so the event travels in the mode just chosen.
    track("consent_choice", { choice: consent, previous });
    setOpen(false);
  }

  return (
    <div className={compact ? "cookie-banner cookie-banner--compact" : "cookie-banner"} role="region" aria-label={t("cookies.label")}>
      <p>
        {compact ? t("cookies.textShort") : t("cookies.text")}{" "}
        {compact ? (
          <a className="auth-inline-link" href={buildPath("privacy", null)} target="_blank" rel="noopener">
            {t("cookies.details")}
          </a>
        ) : (
          <button type="button" className="auth-inline-link" onClick={() => onNavigate("privacy")}>
            {t("footer.privacy")}
          </button>
        )}
      </p>
      <div className="cookie-banner-actions">
        <button type="button" className="secondary" onClick={() => choose("denied")}>
          {t("cookies.decline")}
        </button>
        <button type="button" onClick={() => choose("granted")}>
          {compact ? t("cookies.acceptShort") : t("cookies.accept")}
        </button>
      </div>
    </div>
  );
}
